import { useCallback, useRef } from "react";
import { runStore, getRunState } from "../store/runStore.js";
import { captureChatTarget } from "../lib/forecast/chatTarget.js";
import { execTable } from "../store/execTable.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { branchForecast } from "../commands/branchForecast.js";
import {
  deriveBranch as deriveBranchCommand,
  prefetchDerivedHeadline,
} from "../commands/deriveBranch.js";
import { makeEntry } from "../lib/forecast/model.js";
import {
  customEventPopoverClosed,
  entryAppended,
  forkEventSet,
  runRemoved,
  seriesImpactsSet,
} from "../lib/forecast/reducer.js";
import {
  selectRunTree,
  selectBranchRuns,
  selectRun,
  runToLegacyForecast,
} from "../lib/forecast/selectors.js";
import {
  engineTierKnown,
  isDeepEngine,
  rootEngine,
  sameServableEngine,
  servableEngineLabel,
  tagOwner,
  toServableEngine,
} from "../lib/forecast/engines.js";
import { preprocessTypedTrigger, explainTriggerProblem } from "../lib/forecast/triggerContract.js";
import { scoreTypedTrigger } from "../lib/forecast/triggerScore.js";
import { estimateSeriesImpacts } from "../lib/forecast/forecastClient.js";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import { assessmentText, hasForecastSummaryMessage } from "../lib/forecast/summaryPost.js";
import { extractMultiway } from "../lib/forecast/multiway.js";
import { outcomeSpaceOfRun } from "../lib/forecast/outcomeSpace.js";
import { persistMessage } from "../lib/messages";
import { apiPatch } from "../lib/apiClient";
import { recordUiEvent } from "../lib/logRingBuffer";
import { legacyText, recordTextEvent } from "../lib/telemetry/groups.js";
import { STATUS } from "../lib/forecast/model.js";
import { isDerivable } from "../lib/forecast/derivedBranch.js";

/**
 * React binding + UI adapters for the branch operation (EPIC #157, phase P5.2).
 *
 * The pure exec path moved to `commands/branchForecast.ts`; this hook keeps the parts
 * that MUST stay in React — the clicked-row → parentRunId geometry, popover, and
 * render-state snapshots (`handleBranch`/`handleCustomEventBranch`) plus their small
 * dispatch helpers. `createBranch` here is the thin binding that packs the App's React
 * handles into the command's deps and invokes it. Store access is via `getRunState()` +
 * the store singleton (no `runStateRef`/`dispatchRun` args threaded).
 */
// Module-level stable dispatch (the store singleton's bound dispatch). Kept out of the
// component so it isn't a hook dependency — the adapters' dispatches use it directly.
const dispatch = runStore.dispatch;

/** Preserve the #895 result-based fallback without overwriting a gated scorer direction. */
export function shouldApplyLegacyCustomDirectionFlip({
  parentProbability,
  branchProbability,
  forkEvent,
}) {
  return (
    Number.isFinite(branchProbability) &&
    Number.isFinite(parentProbability) &&
    branchProbability < parentProbability &&
    forkEvent?._scoreStatus !== "coherent"
  );
}

/** Read the current bounded root headline, preferring persisted result authority. */
export function authoritativeRunProbability(run) {
  const persisted = run?.serverPrediction;
  if (Number.isFinite(persisted) && persisted >= 0 && persisted <= 1) return persisted;
  const contracted = run?.contract?.probability;
  if (Number.isFinite(contracted) && contracted >= 0 && contracted <= 1) return contracted;
  return null;
}
export function useBranchForecast(deps) {
  const {
    active,
    compareRoots,
    forecastEngine,
    forecastLiveProgressEnabled,
    guardForTos,
    // async ({ inheritedEngine, pickerEngine }) => boolean — the #5636 confirm surface
    // (useBranchEngineConfirm). Absent means no way to say yes: the guard fails closed.
    confirmBranchEngine,
    persistenceEnabled,
    series,
    setLoading,
    setPendingPreprocess,
    pendingAbortsRef,
    slideCache,
    eventAnchorMs,
    startBranchAnim,
    setLastObs,
    getTourFixture,
    getAuthEnabled,
    getTourBranchIncrease,
    setTourBranchIncrease,
    mockToday,
  } = deps;
  const forecastLiveProgressEnabledRef = useRef(forecastLiveProgressEnabled);
  forecastLiveProgressEnabledRef.current = forecastLiveProgressEnabled;
  // True while a custom-event submission is between entry and its commit point (the ToS
  // fetch and the #5636 confirm are awaits, and the popover now stays open across them —
  // it used to close synchronously, which is what made a second click impossible). A
  // re-entrant call in that window is dropped; cleared on every exit.
  const customBranchPendingRef = useRef(false);
  // The report-headline read a derivation freezes its parent P(YES) from, started when the
  // EventModal opens on a trigger (`prefetchDerivedBranch`, wired as its `onOpen`) so the
  // click does not pay for it. One slot: the modal shows one trigger at a time, and the
  // command re-checks the pointer row and the age before trusting it.
  const derivedPrefetchRef = useRef(null);
  const prefetchDerivedBranch = useCallback((ev, parentRunId) => {
    derivedPrefetchRef.current = parentRunId
      ? prefetchDerivedHeadline(makeDefaultDeps(), {
          parentRunId,
          event: ev,
          current: derivedPrefetchRef.current,
        })
      : null;
  }, []);

  // Thin binding: pack the App's React handles into the command deps and invoke it.
  // Dep array mirrors the original createBranch reactive set.
  const createBranch = useCallback(
    (args) =>
      branchForecast(
        {
          ...makeDefaultDeps(),
          ui: { forecastEngine, forecastLiveProgressEnabled },
          refs: { forecastLiveProgressEnabledRef, slideCache },
          callbacks: { startBranchAnim, setLastObs },
          tour: { getTourFixture, getAuthEnabled, getTourBranchIncrease },
        },
        args,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forecastEngine, forecastLiveProgressEnabled, slideCache, startBranchAnim, setLastObs],
  );

  const createDerivedBranch = useCallback(
    (args) =>
      deriveBranchCommand(
        {
          ...makeDefaultDeps(),
          callbacks: { startBranchAnim },
        },
        args,
      ),
    [startBranchAnim],
  );

  // dispatchGuardLine — shared "engine not initialized" guard tail for
  // handleBranch / handleCustomEventBranch. Both sites build the identical
  // errContent string, wrap it in an assistant entry, resolve the parent
  // conversation from the LIVE store (not a closure), dispatch it, and
  // persist it. Only the boolean condition that triggers the guard differs
  // per site — that stays inline at each call site.
  const dispatchGuardLine = useCallback((parentId, parentForecastId, content) => {
    // Dispatch the guard line into the active conversation so it renders
    // under the projection-only path.
    const guardEntry = makeEntry({ role: "assistant", content, runId: parentId });
    const guardRun = getRunState()?.runsById?.[parentId] ?? null;
    const guardConvId = guardRun?.conversationId ?? parentId;
    if (guardConvId) dispatch(entryAppended(guardConvId, guardEntry));
    persistMessage(parentForecastId, entryToMessageBody(guardEntry));
  }, []);

  // dispatchBlockedLine — shared dispatch tail (no persist) for the
  // no-parent-forecastId guard in handleBranch / handleCustomEventBranch.
  // Message-building (and the recordUiEvent telemetry, whose payload differs
  // per site) stay inline at each call site; this only covers the identical
  // part from entry construction through the store dispatch.
  const dispatchBlockedLine = useCallback((parentId, content) => {
    const blockEntry = makeEntry({ role: "assistant", content, runId: parentId });
    const blockRun = getRunState()?.runsById?.[parentId] ?? null;
    const blockConvId = blockRun?.conversationId ?? parentId;
    if (blockConvId) dispatch(entryAppended(blockConvId, blockEntry));
  }, []);

  // confirmInheritedEngine — the spend guard for #5636, shared by both entry points. A
  // branch runs on its ROOT's engine (rootEngine here; branchForecast.ts stamps run.backend
  // the same way) and the picker is only the fallback — by design (#1980), which means the
  // picker, the one cost control the UI offers, silently does not apply to a branch. So when
  // the engine the branch will use differs from the picker AND is the deep tier in the live
  // registry (by TAG — never a hardcoded lane name), ask BEFORE anything is echoed,
  // persisted, minted or streamed. Resolves true when no question is needed. Fails closed
  // two ways: an unhydrated registry (the tier is unknowable, so the same "initializing"
  // line the engine-init guard uses) and no confirm surface (no way to say yes). A
  // fixture-served tour branch (no spend) skips it. "Differs" is judged on what would
  // RUN (sameServableEngine), so a root stored under a retired id of the picker's own tier
  // is not asked about itself.
  const confirmInheritedEngine = useCallback(
    async ({
      branchEngine,
      pickerEngine,
      parentId,
      parentForecastId,
      kind,
      fixtureServed = !!getTourFixture(),
    }) => {
      const mismatch = !sameServableEngine(branchEngine, pickerEngine);
      if (!mismatch || fixtureServed) return true;
      const telemetry = {
        event: "ui.branch.engine_confirm",
        kind,
        parent_id: parentId,
        inheritedEngine: branchEngine,
        pickerEngine: pickerEngine || null,
        engine_mismatch: mismatch,
      };
      if (!engineTierKnown(branchEngine)) {
        dispatchGuardLine(
          parentId,
          parentForecastId,
          "Sayer is still connecting. Try again in a moment.",
        );
        recordUiEvent({ ...telemetry, tier_known: false, confirmed: false });
        return false;
      }
      if (!isDeepEngine(branchEngine)) return true;
      const confirmed =
        typeof confirmBranchEngine === "function"
          ? (await confirmBranchEngine({ inheritedEngine: branchEngine, pickerEngine })) === true
          : false;
      recordUiEvent({ ...telemetry, tier_known: true, confirmed });
      return confirmed;
    },
    // getTourFixture is a live module-global reader (stable enough); listing it would
    // recreate this callback every render for no behavior gain (§7 incomplete-deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmBranchEngine, dispatchGuardLine],
  );

  // dispatchUserEcho — shared "echo the user's branch action into chat"
  // tail for handleBranch / handleCustomEventBranch: build a user-role
  // entry from the per-site text, resolve the parent conversation from the
  // LIVE store, dispatch it, and persist it (persistMessage always fires so
  // the parent forecast's history captures the action).
  const dispatchUserEcho = useCallback((parentId, parentForecastId, text) => {
    const echoEntry = makeEntry({ role: "user", content: text, runId: parentId });
    const echoRun = getRunState()?.runsById?.[parentId] ?? null;
    const echoConvId = echoRun?.conversationId ?? parentId;
    if (echoConvId) dispatch(entryAppended(echoConvId, echoEntry));
    persistMessage(parentForecastId, entryToMessageBody(echoEntry));
  }, []);

  // postBranchSummary — the completed branch's ASSESSMENT into chat, with the
  // "Full Report" opener, exactly as a root forecast posts on completion.
  //
  // A branch already projected a one-line result ("Branch: <mod> — Prediction: N%") from its
  // terminal run state, but never the assessment prose — so the two kinds of forecast ended
  // differently: submit a question and you got the analysis, branch off one and you got a
  // number. Same resolver (`assessmentText`) as submit and resume, so all three surfaces read
  // the same text.
  //
  // Appends the entry directly rather than going through pushSayerMsg: this hook has no
  // access to that ref, and dispatching `entryAppended` is the same route resumeForecasts
  // takes for the identical post.
  //
  // Routed to the BRANCH's own conversation (falling back to its run id, the same resolution
  // `summaryConvId` uses), so it lands in the thread for the branch the user just made rather
  // than the parent's. Guarded by `hasForecastSummaryMessage` on that run, which is what keeps
  // it to exactly once even if a branch finalizes through more than one path.
  const postBranchSummary = useCallback(
    (branchResult) => {
      if (getTourFixture()) return; // the tour has its own scripted narration
      const runId = branchResult?.runId;
      if (!runId) return;
      if (hasForecastSummaryMessage(getRunState(), runId)) return;
      const assessment = assessmentText(branchResult);
      // Same fallback as submit: a distribution with no assessment still needs the post,
      // because `reportLink` is the only affordance a multiway result has for opening its
      // report (its resolution pie never draws).
      const content =
        assessment || (extractMultiway(branchResult.contract) ? "Forecast complete." : null);
      if (!content) return;
      const entry = makeEntry({
        role: "assistant",
        content,
        runId,
        metadata: { reportLink: true },
      });
      const run = getRunState()?.runsById?.[runId] ?? null;
      const convId = run?.conversationId ?? runId;
      if (convId) dispatch(entryAppended(convId, entry));
      // Persisted against the row the CONVERSATION reloads from, which for a branch is its
      // ROOT — not the branch's own row. The entry is appended to the root's conversation
      // above, and messages are fetched per forecast row, so saving it under the branch left
      // it visible for the session and gone after a reload: the user saw their branch
      // summary, refreshed, and it had vanished.
      const rootForecastId = convId ? selectRun(getRunState(), convId)?.forecastId : null;
      const persistTarget = rootForecastId ?? branchResult.forecastId;
      if (persistTarget) {
        persistMessage(persistTarget, entryToMessageBody(entry));
      }
      // `getTourFixture` is listed because it arrives as a hook argument, not a module import
      // like the rest — leaving it out adds an exhaustive-deps warning, and the eslint gate is
      // a no-increase baseline.
    },
    [getTourFixture],
  );

  // estimateBranchSeriesImpacts — shared branch onComplete tail for handleBranch /
  // handleCustomEventBranch: estimate each active signal's impact under the branch
  // scenario and stash it on the branch run. This drives the branched projection
  // LINES the TSD chart draws per signal — there is intentionally NO chat printout
  // (the impact reads on the chart, not as a "Market impacts" text block). Skipped
  // when no indices are active or during the tour. Fire-and-forget.
  const estimateBranchSeriesImpacts = useCallback(
    (branchResult, signal) => {
      const sKeys = Object.keys(series);
      if (sKeys.length === 0 || getTourFixture()) return;
      estimateSeriesImpacts(branchResult.modification, branchResult.forkDate, sKeys, signal).then(
        (impacts) => {
          if (signal.aborted) return;
          if (!impacts || !Object.keys(impacts).length) return;
          // dual-write series impacts to the store, keyed by the branch RUN id → the
          // TSD chart renders a branched projection line per signal.
          dispatch(seriesImpactsSet(branchResult.runId, impacts));
          // Persist so the branch's dotted projection survives reload. Branches
          // persist via the run path (NOT the roots-only periodic save), and this
          // impact is an async post-completion estimate, so it has no other save
          // hook — PATCH it onto the branch row (stored in server_ctx, pulled back
          // by rowToWire → hydrate). Best-effort via apiPatch (tolerates network
          // failures/401 without throwing; `impacts` is a plain object so the
          // internal JSON.stringify won't throw either): it's already live in the
          // store, and a missing branch row (rare race) just isn't persisted this
          // time.
          // telemetry-no-intent: background persist of an estimate computed after the branch ran
          apiPatch(`/api/forecasts/${encodeURIComponent(branchResult.runId)}`, {
            seriesImpacts: impacts,
            forecastId: getRunState()?.runsById?.[branchResult.runId]?.forecastId,
          });
        },
      );
    },
    // getTourFixture is a live module-global reader (stable enough); listing it would
    // recreate this callback every render for no behavior gain (§7 incomplete-deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series],
  );

  const handleBranch = useCallback(
    async (ev, mt, nv, cancelledRef) => {
      // handleBranch is now a thin ADAPTER over createBranch. Its job is
      // to (a) map the clicked timeline row to the parent RUN id, (b) run the pre-branch
      // guards that are specific to the event-mod entry point, then (c) hand off to the
      // single createBranch exec path. createBranch owns everything downstream: parent
      // resolution from the store, assumption building (triggerToAssumption), placeholder
      // mint, SSE stream, store dual-writes, and the branch chat lines.
      //
      // Tour-only: record whether this branch should raise or lower the forecast,
      // so the baked fixture branch reflects the trigger the user clicked. A
      // probability branch sets nv=1 (Occurs) / 0 (Doesn't); ev.direction "yes"
      // (blue) raises, "no" (red) lowers. occurs⊕yes ⇒ raise.
      if (getTourFixture() && mt === "probability") {
        setTourBranchIncrease(nv >= 0.5 === ((ev?.direction || "yes") === "yes"));
      }
      // cancelledRef up-front guard (preserved from the old signature). The EventModal
      // caller passes a ref; we bail before doing any work if it's already cancelled.
      // NOTE: the only caller (EventModal) resets it to false and never flips it after
      // onBranch, and nothing bridges it onto this branch's AbortController — so today
      // this is a defensive no-op guard, not a live mid-flight abort. A real mid-flight
      // cancel (flip → abort the SSE) would be a future enhancement.
      if (cancelledRef && cancelledRef.current) return;
      // ── Clicked-row → parent RUN id resolution (STAYS in the adapter) ──────────
      // Snapshot the parent forecast's identity BEFORE any await.
      // _rowId is stamped at click time so we can tell a trunk click from a
      // compare-lane / sub-branch click up-front and avoid re-reading `active`
      // after the await (which would attribute the branch to whichever forecast
      // the user is now viewing).
      const clickedRowIdAtTop = ev?._rowId || "main";
      // Compare lanes carry _rowId "compare-<laneIndex>"; sub-branch rows carry
      // "branch-<branchId>". Resolve the OWNING root so the new branch attaches to the
      // correct forecast (design D7), not always `active`:
      //  - "compare-N"  → that lane's root (compareRoots[N]).
      //  - "branch-<id>" → the root whose branches[] contains <id>. Branch ids are
      //    globally unique (every lane renders rowId="branch-"+branch.id), so we scan
      //    every candidate root (active + all compare roots) and pick the one that owns
      //    the clicked branch — that root's branches[] is where the sub-branch lookup must
      //    resolve, NOT always active.branches (which would mis-attribute a sub-branch
      //    forked on a secondary lane to the focused forecast).
      //  - else (main spine) → active.
      const laneMatch = /^compare-(\d+)$/.exec(clickedRowIdAtTop);
      const clickedBranchId = clickedRowIdAtTop.startsWith("branch-")
        ? clickedRowIdAtTop.slice(7)
        : null;
      // Candidate roots to search for a clicked sub-branch's owner, de-duped by id.
      const candidateRoots = [active, ...compareRoots].filter(
        (r, i, arr) => r && arr.findIndex((o) => o && o.id === r.id) === i,
      );
      // Resolve the clicked sub-branch's owner from the run
      // store. r.id is the root runId; selectRunTree(...).slice(1) is the root's branch
      // RUN tree (same order/identity as the legacy r.branches[]); b.id → b.runId.
      const branchOwnerRoot =
        clickedBranchId != null
          ? candidateRoots.find(
              (r) =>
                r &&
                r.id &&
                selectRunTree(getRunState(), r.id)
                  .slice(1)
                  .some((b) => b.runId === clickedBranchId),
            ) || null
          : null;
      const laneRoot = laneMatch ? compareRoots[Number(laneMatch[1])] : null;
      const branchParent = branchOwnerRoot || laneRoot || active;
      const parentId = branchParent?.id || null;
      // Sub-branch lookup resolves against the owning root's branch RUN tree, not active's.
      // The matched element is a run object: id → runId, forecastId maps 1:1.
      const parentBranchAtClick =
        clickedBranchId != null
          ? selectBranchRuns(getRunState(), branchParent?.id).find(
              (b) => b.runId === clickedBranchId,
            ) || null
          : null;
      // The parent RUN id we hand to createBranch: a sub-branch click parents off the
      // clicked branch's own run id; a trunk / compare-lane click parents off the root's
      // run id (= conversationId). createBranch re-resolves forecastId + conversationId +
      // parentBranchId from this run id via runStateRef — the headline store-sourced fix.
      const parentRunId =
        (parentBranchAtClick ? parentBranchAtClick.runId : branchParent?.id) || null;
      // ── Pre-branch guards (event-mod entry point) ─────────────────────────────
      // Resolve the parent's server forecastId from the LIVE store (not the closure)
      // FOR THE GUARDS BELOW only. createBranch resolves its own copy independently;
      // these reads exist solely so the adapter can fail fast with a clear message
      // before claiming an exec slot.
      const liveParentForecastId = parentRunId
        ? selectRun(getRunState(), parentRunId)?.forecastId || null
        : null;
      const parentForecastId =
        liveParentForecastId ||
        (parentBranchAtClick ? parentBranchAtClick.forecastId : branchParent?.forecastId) ||
        null;
      const liveParentRun = parentRunId ? selectRun(getRunState(), parentRunId) : null;
      // Engine this branch runs on — resolved EXACTLY as createBranch resolves it (the
      // PARENT run's durable backend, then its metadata; the picker only as fallback), from
      // the same live run it will read (selectRun(parentRunId)), and then handed to it as
      // `opts.engine` so the value gated and shown here is the value dispatched. Reading the
      // ROOT's engine here would be wrong for a sub-branch click: the parent is the clicked
      // branch run, and a branch minted before inheritance (#1980) carries the picker of its
      // day as `backend` — a deep legacy branch under a quick root would slip the gate. The
      // render-scope root is the last resort for a parent not in the store (tour fixture).
      const inheritedEngine =
        liveParentRun?.backend ||
        rootEngine(liveParentRun) ||
        rootEngine(parentBranchAtClick) ||
        rootEngine(branchParent) ||
        null;
      const branchEngine = inheritedEngine || forecastEngine;
      // The id the request will actually carry — the same re-pointing the tier check
      // applies — pinned into createBranch so the wire engine is the one tier-checked here.
      const servableEngine = toServableEngine(branchEngine);
      // A complete-on-creation machine branch never invokes a forecaster. Decide
      // that before the engine guard so a hydrated/legacy root without persisted
      // orchestrator metadata can still use its already-materialized a/b values.
      const deriveWithoutRun = isDerivable(ev, liveParentRun, mt, nv);
      // Engine-initialized guard. /api/forecast/health populates forecastEngine
      // after page load; branch triggers live on the timeline and are clickable from
      // the moment forecasts render, so a click in the post-reload gap could send an
      // empty `orchestrator`. Guard on branchEngine (the value the request actually
      // uses) so a post-reload branch still works when the root carries a persisted
      // orchestrator before /health has repopulated forecastEngine.
      if (!branchEngine && !deriveWithoutRun) {
        dispatchGuardLine(
          parentId,
          parentForecastId,
          "Sayer is still connecting. Try again in a moment.",
        );
        return;
      }
      // Guard: a branch run-start REQUIRES parent_forecast_id — the streaming proxy
      // 400s ("branch requires parent_forecast_id") without it. A null here means the
      // parent forecast/branch has no server id: it errored, never finished, or came
      // back id-less after a reload. Firing anyway yields a confusing 400 and a stuck
      // placeholder, so surface a clear message and bail instead. Tour exception: the
      // baked fixture root never gets a server forecastId (it's instant, so
      // onTraceAllocated never fires) and createBranch is fixture-served without ever
      // hitting the proxy — so the guard is a false positive that would stall the tour.
      if (!parentForecastId && !getTourFixture()) {
        const parentStatus = liveParentRun?.status || null;
        const stillGenerating =
          parentStatus === STATUS.RUNNING || parentStatus === STATUS.PREPROCESSING;
        const label = parentBranchAtClick?.modification || "that forecast";
        const blockContent = stillGenerating
          ? `"${label}" is still running. Wait for it to finish, then explore a scenario.`
          : !persistenceEnabled
            ? `Scenarios aren't available in this local setup because forecasts aren't being saved here. They work on the deployed app.`
            : `Can't explore a scenario on "${label}" because it didn't finish. Run it again first.`;
        recordUiEvent({
          event: "ui.branch.blocked.no_parent_fc",
          parent_id: parentId,
          clicked_branch_id: clickedBranchId,
          parent_status: parentStatus,
        });
        dispatchBlockedLine(parentId, blockContent);
        return;
      }
      if (!(await guardForTos())) return;
      // A derived branch invokes no forecaster, so there is nothing to confirm — unless it
      // falls back to a real run below, which asks then.
      if (
        !deriveWithoutRun &&
        !(await confirmInheritedEngine({
          branchEngine,
          pickerEngine: forecastEngine,
          parentId,
          parentForecastId,
          kind: mt,
        }))
      )
        return;
      recordUiEvent({
        event: "ui.branch.create",
        kind: mt === "probability" ? (nv >= 0.5 ? "occurs" : "not_occurs") : mt,
        event_id: ev?.id || null,
        event_title: (ev?.title || "").slice(0, 80),
        parent_id: parentId,
        parent_forecast_id: parentForecastId,
        engine: servableEngine,
        inheritedEngine,
        pickerEngine: forecastEngine || null,
        engine_mismatch: !sameServableEngine(branchEngine, forecastEngine),
        derived: deriveWithoutRun,
      });
      // ── Hand off to the single createBranch exec path ─────────────────────────
      // One AbortController shared between exec-cancel (abortController) and stream-abort
      // (signal) — the two must be the same controller so a cancel actually aborts the
      // in-flight SSE. Tracked in pendingAbortsRef like every other in-flight branch.
      const ac = new AbortController();
      pendingAbortsRef.current.add(ac);
      setLoading(true);
      // event-mod trigger: createBranch + triggerToAssumption build the assumption,
      // modification, and fork event from this descriptor (verbatim genBranch parity).
      const evCopy = JSON.parse(JSON.stringify(ev));
      // Anchored fork position (override): eventAnchorMs is the SAME anchor the timeline
      // draws the event circle at (mean_date → slideCache → range-midpoint → date), so the
      // vertical fork connector lands on the circle. triggerToAssumption is pure and can't
      // read slideCache.current, so the adapter computes it here and passes it as an
      // override. Match OLD handleBranch EXACTLY: ms number → ISO date string (YYYY-MM-DD),
      // the same type branch.forkDate / makeRun({forkDate}) / the fork-connector render consume.
      const forkDate = new Date(eventAnchorMs(evCopy, clickedRowIdAtTop))
        .toISOString()
        .slice(0, 10);
      const trigger = { kind: "event-mod", event: evCopy, modType: mt, newValue: nv, forkDate };
      // F5: echo the user's branch action to the chat + persist it BEFORE handing off,
      // so the parent forecast's history records "they tried Y on X" even mid-branch
      // (parity with old handleBranch). This `desc` is the USER-facing action label —
      // distinct from the `modification` triggerToAssumption builds inside createBranch.
      const desc = (() => {
        if (mt === "delete") return `Remove: "${evCopy.title}"`;
        if (mt === "date") return `Move "${evCopy.title}" to ${nv}`;
        if (mt === "probability")
          return nv >= 0.5 ? `If "${evCopy.title}" happens` : `If "${evCopy.title}" doesn't happen`;
        return `Change "${evCopy.title}" chance to ${Math.round(nv * 100)}%`;
      })();
      dispatchUserEcho(parentId, parentForecastId, desc);
      let fallbackIntent = null;
      let fallbackRunId = null;
      try {
        if (deriveWithoutRun) {
          const result = await createDerivedBranch({
            parentRunId,
            event: evCopy,
            newValue: nv,
            forkDate,
            prefetch: derivedPrefetchRef.current,
            signal: ac.signal,
            onComplete: (branchResult) => {
              postBranchSummary(branchResult);
            },
          });
          if (!result.fallback) return;
          // The model run below continues the derivation's branch intent (one click, one
          // count); a guard that stops it here ends that intent.
          fallbackIntent = result.intent ?? null;
          fallbackRunId = result.clientId ?? null;
          recordUiEvent({
            event: "ui.branch.derived_fallback",
            event_id: evCopy.id || null,
            parent_id: parentId,
            parent_forecast_id: parentForecastId,
          });
          // The engine guard above is intentionally bypassed only for the no-run
          // derived path. If server authority rejects the frozen snapshot, the
          // fallback is an ordinary streamed branch and must satisfy the guard.
          if (!branchEngine) {
            dispatchGuardLine(
              parentId,
              parentForecastId,
              "Sayer is still connecting. Try again in a moment.",
            );
            fallbackIntent?.end("error");
            return;
          }
          // Same for the #5636 spend confirm: skipped above because a derived branch spends
          // nothing; the fallback is a real run on the inherited engine, so ask now. The
          // user's action is already echoed into chat (F5, above) — so a "no" here answers
          // it, as the init guard's line does, rather than leaving the echo hanging.
          if (
            !(await confirmInheritedEngine({
              branchEngine,
              pickerEngine: forecastEngine,
              parentId,
              parentForecastId,
              kind: mt,
            }))
          ) {
            dispatchBlockedLine(
              parentId,
              `Scenario not started. It would have used ${servableEngineLabel(branchEngine)}, like the main forecast.`,
            );
            fallbackIntent?.end("cancelled");
            return;
          }
        }
        await createBranch({
          parentRunId,
          trigger,
          opts: {
            signal: ac.signal,
            abortController: ac,
            ...(fallbackIntent ? { intent: fallbackIntent, branchRunId: fallbackRunId } : {}),
            // The engine gated + shown above; createBranch stamps and sends this one.
            engine: servableEngine,
            // No per-site onProgress: createBranch owns the "Generating Branch"
            // placeholder line (keyed by the branch run id it mints internally and
            // does not surface), so the adapter has no line to decorate. Progress
            // display is createBranch's responsibility, not handleBranch's.
            // Per-site post-completion hook: estimate series impacts if indices are
            // active. Orthogonal to branch creation (createBranch has already written
            // the COMPLETED run to the store). Skipped during the tour. Fire-and-forget
            // — onComplete returns immediately; the .then runs detached, same as before.
            onComplete: (branchResult) => {
              postBranchSummary(branchResult);
              estimateBranchSeriesImpacts(branchResult, ac.signal);
            },
          },
        });
      } finally {
        pendingAbortsRef.current.delete(ac);
        // Reconcile the global busy flag against what's actually in flight rather
        // than skipping the clear on abort. The old `if(!ac.signal.aborted)` left
        // `loading` stuck TRUE forever when a branch was cancelled with nothing
        // taking over — silently wedging the REFORECAST button (and submit). The
        // Ordinary runs claim the exec table; no-run derived requests are represented
        // by their registered controllers. Loading clears only when BOTH sources are
        // empty, so one derived completion cannot report idle while another is pending.
        setLoading(execTable.size() > 0 || pendingAbortsRef.current.size > 0);
      }
      // eventAnchorMs is a render-scope helper that reads the slideCache ref each call,
      // so it is intentionally NOT a dependency — listing it would recreate handleBranch
      // every render and defeat the memo (the live ref is read at call time regardless).
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      active,
      forecastEngine,
      guardForTos,
      compareRoots,
      createBranch,
      createDerivedBranch,
      dispatchGuardLine,
      dispatchBlockedLine,
      dispatchUserEcho,
      confirmInheritedEngine,
      estimateBranchSeriesImpacts,
      persistenceEnabled,
    ],
  );

  const handleCustomEventBranch = useCallback(
    async (
      description,
      date,
      { ownerRunId, onCreated, onEnriched, onRejected, tier = null } = {},
    ) => {
      // Thin ADAPTER over createBranch (see handleBranch above): here the pre-branch
      // guards are specific to the custom-event entry point (no-active, engine-init,
      // no-parent-forecastId, ToS, the deep-inheritance confirm), plus closing the popover.
      //
      // The popover is closed HERE, not by the caller, so that a declined confirm leaves the
      // typed description on screen: every guard that ends the attempt closes it (as the
      // caller used to, unconditionally); the confirm's "no" — and its "try again in a
      // moment" — keep it open. `onCreated` / `onEnriched` are threaded through to
      // createBranch: the first fires once the run EXISTS, the second once its trigger has
      // become a contract. `onRejected({ description, date, message, ownerRunId })` fires when
      // the typed text cannot become a contract — every trigger must be one, so no branch is
      // made and the caller can hand the text back for editing, on the attempt's own run.
      //
      // `ownerRunId` is the run being branched — the run whose popover this is, or the one a
      // chat `add_event` was sent on — and the popover closed here is that run's. It is never
      // read from the selection, which may have moved since the attempt started: a run that
      // is gone is refused, visibly, rather than replaced by whatever is selected now.
      //
      // Snapshot identity BEFORE any await — see [[handleBranch]] for rationale. Custom
      // branches always root off the trunk (no sub-branch path through this handler), so
      // its forecastId is the parent forecast id.
      const ownerRun = ownerRunId ? selectRun(getRunState(), ownerRunId) : null;
      const parent = ownerRun
        ? runToLegacyForecast(ownerRun, getRunState(), forecastLiveProgressEnabledRef.current)
        : null;
      if (!parent) {
        recordUiEvent({
          event: "ui.branch.blocked.owner_gone",
          kind: "custom",
          owner_run_id: ownerRunId ?? null,
        });
        // The owner run is gone, so the line goes to the thread on screen now (captured here,
        // the sanctioned way to read the selection).
        const here = captureChatTarget(getRunState()).runId;
        if (here)
          dispatchBlockedLine(
            here,
            "The forecast that trigger was typed for is no longer loaded, so no branch was created.",
          );
        return;
      }
      const closePopover = () => runStore.dispatch(customEventPopoverClosed(ownerRunId));
      const parentId = parent.id;
      // Live-read the root's forecastId from the store, not the closure `active`
      // (which can lag when the id was stamped after this callback memoized — the
      // same stale-closure 400 fixed in handleBranch). createBranch re-resolves its own
      // copy from the store keyed by parentRunId; this read is for the fail-fast guards
      // below only.
      const liveParentRun = parentId ? selectRun(getRunState(), parentId) : null;
      const parentForecastId = liveParentRun?.forecastId || parent.forecastId || null;
      // Full click-time snapshot for the engine resolution below (.orchestrator via
      // rootEngine), so a mid-await forecast switch can't substitute the new active's
      // engine into the guard/telemetry.
      const activeAtClick = JSON.parse(JSON.stringify(parent));
      if (!description.trim()) return;
      // The engine this branch will run on — resolved as createBranch resolves it (the live
      // parent run's durable backend, then its metadata, then the click-time snapshot; the
      // picker only as fallback). `servableEngine` is the id the request will actually
      // carry — the same re-pointing the tier check applies — and is what createBranch is
      // handed as `opts.engine`, so what is gated and shown below is what is dispatched
      // (see handleBranch).
      const inheritedEngine =
        liveParentRun?.backend || rootEngine(liveParentRun) || rootEngine(activeAtClick) || null;
      // A depth picked in the trigger box (Quick by default) wins over the parent's engine.
      const pickedEngine = tier ? tagOwner(tier) : null;
      const branchEngine = pickedEngine || inheritedEngine || forecastEngine;
      const servableEngine = toServableEngine(branchEngine);
      // The tour fixture has canned results for the tour's own triggers and none for text
      // the user typed, so a typed trigger streams for real whenever the root has a server
      // row to branch from. Without one (auth off) it stays fixture-served.
      const live = !!parentForecastId;
      // Re-entrancy guard for the pre-commit window (see customBranchPendingRef).
      if (customBranchPendingRef.current) {
        recordUiEvent({
          event: "ui.branch.blocked.in_flight",
          kind: "custom",
          parent_id: parentId,
        });
        return;
      }
      customBranchPendingRef.current = true;
      try {
        // Engine-initialized guard — same rationale as handleBranch above. Guard on the
        // RESOLVED engine the request uses, not forecastEngine alone, so a custom-event
        // branch isn't blocked in the post-reload gap when the root has a persisted
        // orchestrator.
        if (!branchEngine) {
          closePopover();
          dispatchGuardLine(
            parentId,
            parentForecastId,
            "Sayer is still connecting. Try again in a moment.",
          );
          return;
        }
        // Guard (mirrors handleBranch): a custom-event branch run-start REQUIRES
        // parent_forecast_id. A null root id (root errored / never finished / came back
        // id-less after a reload) would 400 with a stuck placeholder — bail with a
        // clear message instead. Tour exception (same as handleBranch): the fixture root
        // has no server forecastId and the branch is fixture-served without a proxy call.
        if (!parentForecastId && !getTourFixture()) {
          const blockContent = !persistenceEnabled
            ? `Scenarios aren't available in this local setup because forecasts aren't being saved here. They work on the deployed app.`
            : `Can't explore a scenario on "${parent.question || "that forecast"}" because it didn't finish. Run it again first.`;
          recordUiEvent({
            event: "ui.branch.blocked.no_parent_fc",
            parent_id: parentId,
            kind: "custom",
          });
          closePopover();
          dispatchBlockedLine(parentId, blockContent);
          return;
        }
        if (!(await guardForTos())) {
          closePopover();
          return;
        }
        // Confirm BEFORE the popover closes or anything is echoed.
        if (
          !pickedEngine &&
          !(await confirmInheritedEngine({
            branchEngine,
            pickerEngine: forecastEngine,
            parentId,
            parentForecastId,
            kind: "custom",
            fixtureServed: !live,
          }))
        )
          return;
        // Committed: every guard passed and any question was answered yes.
        closePopover();
      } finally {
        customBranchPendingRef.current = false;
      }
      // Normalize the picked date to the YYYY-MM-DD the trigger + telemetry + echo line use.
      const dateStr = new Date(date).toISOString().slice(0, 10);
      recordUiEvent({
        event: "ui.branch.create",
        kind: "custom",
        description: legacyText(description),
        date: dateStr,
        parent_id: parentId,
        parent_forecast_id: parentForecastId,
        engine: servableEngine,
        inheritedEngine,
        pickerEngine: forecastEngine || null,
        engine_mismatch: !sameServableEngine(branchEngine, forecastEngine),
      });
      recordTextEvent({
        event: "text.trigger_description",
        value: description,
        len: description.length,
        forecast_id: parentForecastId ?? null,
        parent_id: parentId ?? null,
      });
      dispatchUserEcho(
        parentId,
        parentForecastId,
        `Explore a scenario: "${description.trim()}" on ${dateStr}`,
      );
      // The preprocess arguments, hoisted so the pre-run check below and `enrichTrigger`
      // cannot drift — a mismatch would preprocess the same trigger twice, differently.
      const preArgs = {
        typed: description,
        parent: activeAtClick?.contract || null,
        forkDate: dateStr,
        asOf: (mockToday ? new Date(mockToday) : new Date()).toISOString().slice(0, 10),
        nowMs: (mockToday ? new Date(mockToday) : new Date()).getTime(),
      };
      // A PAST placement is checked BEFORE the run starts, and refused if its deadline has
      // gone by. What decides gradability is the trigger's contract CLOSE, not where the
      // node sits — and only the preprocessor knows it — so for a past node the contract is
      // resolved up front rather than after (`validateTriggerContract`'s `in_past`). A
      // branch past its close can never be minted, so no verdict can ever exist for it, so
      // its premise could never settle: it would sit on the timeline as a live conditional
      // forever. Better to spend one preprocess call than a whole forecast run on a branch
      // that is dead on arrival.
      //
      // A FUTURE placement is untouched and keeps the draw-first order (node and dotted
      // lines on screen before the LLM call) — the common case, and the reason enrichTrigger
      // runs after createBranch at all.
      // Cancellation covers pre-run validation as well as enrichment and streaming.
      const ac = new AbortController();
      pendingAbortsRef.current.add(ac);
      // The text cannot become a contract, so it cannot be a trigger: say why, and hand the
      // text back. `close` is the deadline a rejected contract came back with (telemetry).
      const rejectTrigger = (problem, close) => {
        recordUiEvent({
          event: "ui.branch.trigger_contract_rejected",
          kind: "custom",
          problem,
          returned_close: close || null,
          parent_close: activeAtClick?.contract?.resolution_date || null,
          parent_id: parentId,
          parent_forecast_id: parentForecastId,
        });
        const message = explainTriggerProblem(problem, activeAtClick?.contract || null);
        dispatchGuardLine(parentId, parentForecastId, message);
        try {
          onRejected?.({ description, date, message, ownerRunId });
        } catch (_e) {
          /* caller-side only */
        }
      };
      // One retry for a transport failure: it says nothing about the text, and a single
      // blip should not cost the user their branch.
      const preprocessOnce = () => preprocessTypedTrigger({ ...preArgs, signal: ac.signal });
      const preprocessWithRetry = async () => {
        const first = await preprocessOnce();
        if (first.ok || first.problem !== "request_failed" || ac.signal.aborted) return first;
        return preprocessOnce();
      };
      try {
        let prefetched = null;
        // A fixture-served branch (the auth-off tour: no server row, so no mint and no
        // grading) has no contract to require; only a live branch is checked up front.
        if (live && preArgs.nowMs >= new Date(date).getTime()) {
          prefetched = await preprocessWithRetry();
          if (ac.signal.aborted) return;
          if (!prefetched.ok) {
            if (prefetched.problem === "in_past") {
              recordUiEvent({
                event: "ui.branch.blocked.trigger_closed",
                kind: "custom",
                date: dateStr,
                parent_id: parentId,
                parent_forecast_id: parentForecastId,
              });
            }
            rejectTrigger(prefetched.problem, prefetched.close);
            return;
          }
        }
        setLoading(true);
        // custom-event trigger: createBranch + triggerToAssumption build the assumption,
        // modification, placeholder fork event, and forkDate from this descriptor.
        // No contract yet — deliberately. The trigger goes in RAW so createBranch can draw
        // the node and its dotted lines immediately; the preprocessor runs in `enrichTrigger`
        // below, once the branch is already on screen.
        const trigger = { kind: "custom-event", description, date };
        await createBranch({
          parentRunId: parentId,
          trigger,
          opts: {
            signal: ac.signal,
            abortController: ac,
            origin: "custom-trigger.branch",
            // The engine gated + shown above; createBranch stamps and sends this one.
            engine: servableEngine,
            live,
            // Fires once the replacement run is minted + claimed (see the adapter comment).
            onCreated,
            onEnriched,
            // Every trigger is a contract: an enrichment that yields none removes the
            // placeholder instead of streaming a contract-less branch. A fixture-served
            // branch is never minted, so it streams as before when preprocessing fails.
            enrichRequired: live,
            // Reform the typed text into a real CONTRACT — the same preprocessor a typed
            // forecast question goes through, so the trigger gets a self-contained question,
            // resolution criteria and a close date, which is what makes it mintable and the
            // premise gradable. Composed WITH the parent (see triggerContract.ts): alone the
            // writer has no domain, no timeframe and no upper bound, and a trigger resolving
            // after its parent cannot inform it.
            //
            // Runs HERE rather than before createBranch so the node and its dotted lines are
            // already drawn when this LLM call starts. Returning null removes the branch
            // (`enrichRequired`).
            enrichTrigger: async (raw, branchRunId) => {
              // Already resolved above for a past placement — reuse it rather than paying
              // for the same call twice (and risking two different answers).
              const pre = prefetched ?? (await preprocessWithRetry());
              if (pre.ok) {
                // Surface the rephrasing for review, exactly as a top-level submission does.
                // The preprocessor turns a few typed words into a full contract question, and
                // it can get the claim subtly wrong — a wrong bound, the wrong entity — with
                // nothing on screen to reveal it. The panel gate already admits a branch (it
                // matches any branch run beneath the active root), so this is the same panel,
                // keyed to the branch's own run.
                //
                // The branch is already drawn. Accepting changes nothing; editing cancels
                // this branch and re-runs it from the corrected text.
                setPendingPreprocess({
                  original: description,
                  question: pre.preprocessed,
                  engine: servableEngine,
                  editing: false,
                  editText: pre.preprocessed?.question_text || description,
                  clientId: branchRunId,
                  // Marks this panel as a typed TRIGGER rather than a forecast question, and
                  // carries the one fact a re-run needs that the text does not: where on the
                  // timeline the user put it.
                  // The INSTANT, not `dateStr`: an edit-resubmit feeds this straight back
                  // into handleCustomEventBranch, whose live caller passes
                  // resolveTriggerInstant(...). Handing back the day string would re-anchor
                  // the re-run at UTC midnight and move the node off the user's click.
                  // `date` is kept alongside for display.
                  branchTrigger: { instant: date, date: dateStr },
                });
                // Score the trigger for the ROOT TIMELINE only. This gated triple is
                // deliberately independent of the branch run: createBranch still builds
                // branch_context from the original raw custom-event descriptor, whose
                // trigger_a/trigger_b stay null.
                const parentProbability = authoritativeRunProbability(activeAtClick);
                const score = await scoreTypedTrigger({
                  parent: activeAtClick,
                  p0: parentProbability,
                  report: activeAtClick?.report ?? activeAtClick?.serverContext?.report ?? null,
                  contract: pre.contract,
                  placedOn: dateStr,
                  outcomeSpace: outcomeSpaceOfRun(activeAtClick),
                  signal: ac.signal,
                });
                return { ...raw, contract: pre.contract, score };
              }
              // An abort is the user's own edit or cancel — nothing to explain. A fixture
              // branch still streams (enrichRequired is off), so it is not rejected either.
              if (!ac.signal.aborted && live) rejectTrigger(pre.problem, pre.close);
              return null;
            },
            resultLabel: `Scenario: "${description.trim()}"`,
            errorLabel: "Couldn't start this scenario. ",
            // Per-site post-completion hook. The branch run is already COMPLETED in the
            // store (createBranch dual-wrote it); this layers per-site behavior on top.
            onComplete: (branchResult) => {
              postBranchSummary(branchResult);
              // Custom-event direction-flip (#895): default minted direction is "yes"
              // (blue/above); flip to "no" (red/below) when the branch's result probability
              // comes in below the parent's, so ForecastLane's render reflects the drop.
              // Resolve the parent from the live store, never a captured closure.
              // `selectRun`, not `runsById` — a SHARED parent lives in its own slice, and
              // reading only the owner leg silently skipped the flip on every branch off
              // a forecast shared with the viewer.
              const parentRun = selectRun(getRunState(), parentId);
              const branchProb = branchResult.contract?.probability ?? branchResult.probability;
              const parentProb = authoritativeRunProbability(parentRun);
              // Require both to be finite before comparing — `null < 0.5` coerces to
              // `0 < 0.5` (true), which would spuriously flip on a missing probability.
              const dropped = shouldApplyLegacyCustomDirectionFlip({
                parentProbability: parentProb,
                branchProbability: branchProb,
                forkEvent: branchResult.forkEvent,
              });
              if (dropped && branchResult.forkEvent) {
                dispatch(
                  forkEventSet(branchResult.runId, { ...branchResult.forkEvent, direction: "no" }),
                );
              }
              //
              // Series-impact estimation (mirrors handleBranch): estimate series impacts
              // if indices are active. Orthogonal to branch creation. Skipped during the
              // tour. Fire-and-forget — the .then runs detached, same as before.
              estimateBranchSeriesImpacts(branchResult, ac.signal);
            },
          },
        });
      } finally {
        pendingAbortsRef.current.delete(ac);
        // Reconcile the global busy flag against what's actually in flight rather
        // than skipping the clear on abort. The old `if(!ac.signal.aborted)` left
        // `loading` stuck TRUE forever when a branch was cancelled with nothing
        // taking over — silently wedging the REFORECAST button (and submit). The
        // Ordinary runs claim the exec table; preprocessing and no-run derived work
        // is represented by its registered controller before any exec claim exists.
        // Both adapters must reconcile both sources or one custom rejection can report
        // idle while a concurrent derived request is still pending.
        setLoading(execTable.size() > 0 || pendingAbortsRef.current.size > 0);
      }
    },
    // The tour getter + the setters/refs passed in (setLoading/
    // pendingAbortsRef) are stable-in-practice; listing the inline getter thunk would
    // recreate the callback every render (§7 incomplete-deps, mirrors handleBranch above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      forecastEngine,
      guardForTos,
      createBranch,
      dispatchGuardLine,
      dispatchBlockedLine,
      dispatchUserEcho,
      confirmInheritedEngine,
      estimateBranchSeriesImpacts,
      persistenceEnabled,
    ],
  );

  // Re-run a typed-trigger branch from corrected text. The panel is non-blocking, so by the
  // time the user edits, the branch is on screen and may still be enriching — this drops that run
  // and starts a clean one from the same click position, which is what `handleBranch`'s
  // sibling `editResubmit` does for a top-level submission.
  //
  // The old run is REMOVED rather than left beside the new one: the user corrected a claim,
  // they did not ask for a second branch, and leaving both would put a trigger on the
  // timeline they had just said was wrong.
  //
  // Removed only once the replacement's trigger has become a CONTRACT (createBranch's
  // `onEnriched`, threaded through handleCustomEventBranch: the new run exists, its stream
  // not yet open): a declined confirm, a blocked guard, a corrected text the preprocessor
  // rejects, an abort or a failed mint all leave the existing branch standing rather than
  // deleting it and creating nothing. The two coexist while the correction is preprocessed;
  // that beats the alternative, a timeline with neither. Abort-before-remove ordering is
  // preserved inside the callback. A rejected correction reaches `onRejected`, so the caller
  // can hand the corrected text back for another edit.
  const rerunCustomEventBranch = useCallback(
    (pp, { onRejected } = {}) => {
      const text = (pp?.editText || "").trim();
      const placement = pp?.branchTrigger?.instant ?? pp?.branchTrigger?.date;
      if (!text || !placement) return Promise.resolve();
      const staleRunId = pp.clientId;
      return handleCustomEventBranch(text, placement, {
        // The correction re-runs under the edited branch's own parent.
        ownerRunId: staleRunId ? selectRun(getRunState(), staleRunId)?.parentRunId : null,
        onEnriched: () => {
          if (!staleRunId) return;
          // Cancel pending enrichment/streaming before removing the run.
          execTable.abort(staleRunId);
          runStore.dispatch(runRemoved(staleRunId));
        },
        onRejected,
      });
    },
    [handleCustomEventBranch],
  );

  return { handleBranch, handleCustomEventBranch, rerunCustomEventBranch, prefetchDerivedBranch };
}
